/**
 * 표시용 포맷터.
 *
 * 🔴 환율은 `voxctx/money.py` 의 1 USD = 1,500원 고정을 그대로 따릅니다.
 *    화면에서 환율을 다시 계산하지 않습니다 — 서버가 이미 `usd` 와 `krw` 를
 *    함께 내려주므로, 여기서는 **받은 값을 표기만** 합니다.
 */

/** 서버가 usd 만 준 자리에서 어림값을 보여줄 때만 씁니다. 정본은 서버 값입니다. */
export const USD_TO_KRW = 1500

/** `1,234원` */
export function formatKrw(krw: number): string {
  return `${Math.round(krw).toLocaleString('ko-KR')}원`
}

/** `$0.1120` — 전사 요금은 소수점 아래가 의미를 가지므로 4자리까지 봅니다. */
export function formatUsd(usd: number): string {
  return `$${usd.toLocaleString('en-US', {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  })}`
}

/** `168원 ($0.1120)` — 원화 우선, USD 병기. */
export function formatMoney(krw: number, usd: number): string {
  return `${formatKrw(krw)} (${formatUsd(usd)})`
}

/** `315원/시간` 같은 시간당 요율. */
export function formatRateKrw(krwPerHour: number): string {
  return `${formatKrw(krwPerHour)}/시간`
}

/**
 * `1:23:45` (1시간 이상) / `12:34` (1시간 미만)
 * 미디어 시각 표기의 정본입니다.
 */
export function formatTimecode(ms: number, alwaysHours = false): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const mm = String(m).padStart(2, '0')
  const ss = String(s).padStart(2, '0')
  if (h > 0 || alwaysHours) return `${h}:${mm}:${ss}`
  return `${m}:${ss}`
}

/** `1:23:45.678` — 단어 타임스탬프처럼 밀리초까지 봐야 할 때. */
export function formatTimecodeMs(ms: number): string {
  const frac = String(Math.max(0, Math.floor(ms)) % 1000).padStart(3, '0')
  return `${formatTimecode(ms, true)}.${frac}`
}

/** `1:23:45 ~ 1:25:10` */
export function formatTimeRange(startMs: number, endMs: number): string {
  return `${formatTimecode(startMs)} ~ ${formatTimecode(endMs)}`
}

/**
 * `11시간 54분` / `32분 10초` / `48초`
 * 길이(기간)를 사람이 읽는 단위로 씁니다. 시각과 헷갈리지 않게 단위를 붙입니다.
 */
export function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  if (h > 0) return m > 0 ? `${h}시간 ${m}분` : `${h}시간`
  if (m > 0) return s > 0 ? `${m}분 ${s}초` : `${m}분`
  return `${s}초`
}

/** `1.4 GB` / `328 MB` / `12 KB` */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const units = ['KB', 'MB', 'GB', 'TB']
  let value = bytes / 1024
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value.toFixed(value >= 100 ? 0 : 1)} ${units[unit]}`
}

/** `95.5%` — 0..1 비율을 백분율로. */
export function formatRatio(ratio: number, digits = 1): string {
  return `${(ratio * 100).toFixed(digits)}%`
}

const DB_DATETIME = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/

/**
 * `2026-08-10 08:12`
 *
 * SQLite 가 `datetime('now','localtime')` 로 넣은 문자열에는 타임존이 없습니다.
 * `new Date()` 에 넘기면 브라우저마다 UTC 로 해석해 시각이 밀리므로,
 * **문자열을 그대로 잘라 씁니다.**
 *
 * 형식이 다르면 지어내지 않고 받은 값을 그대로 돌려줍니다.
 */
export function formatDateTime(value: string | null | undefined, withSeconds = false): string {
  if (!value) return '—'
  const m = DB_DATETIME.exec(value)
  if (!m) return value
  const [, y, mo, d, h, mi, s] = m
  const base = `${y}-${mo}-${d} ${h}:${mi}`
  return withSeconds && s ? `${base}:${s}` : base
}

/** `08:12:03` — 같은 날 안에서 이벤트 순서를 볼 때. */
export function formatClock(value: string | null | undefined): string {
  if (!value) return '—'
  const m = DB_DATETIME.exec(value)
  if (!m) return value
  return `${m[4]}:${m[5]}:${m[6] ?? '00'}`
}

/** `82단어/분` — 자막 밀도처럼 약한 구간 판별 근거를 보여줄 때. */
export function formatWordsPerMin(wpm: number): string {
  return `${wpm.toFixed(0)}단어/분`
}

/** 큰 정수를 `12,345` 로. */
export function formatCount(n: number): string {
  return n.toLocaleString('ko-KR')
}
